/**
 * 智能缓存管理器
 * 监听音频和视频的加载完成状态，自动缓存用户喜欢的歌曲
 * 当音频或视频文件加载完毕时，立即自动缓存到浏览器
 */

import musicCache from '@/utils/musicCache';
import videoCacheService from '@/services/VideoCacheService.js';
import { isVideoSongAutoCacheEnabled } from '@/services/UnifiedVideoCacheManager.js';
import * as tokenService from '@/utils/tokenService';
import myRequest from '@/utils/myRequest';
import { retypeAudioBlob } from '@/utils/audioFormatSniff';

class SmartCacheManager {
  constructor() {
    this.audioLoadStates = new Map(); // 存储音频加载状态
    this.videoLoadStates = new Map(); // 存储视频加载状态
    this.cacheThreshold = 0.95; // 视频缓存：加载完成阈值（95%）
    /** 用户「喜欢」：播放进度超过 50%，或累计收听超过 15 秒 */
    this.likeProgressThreshold = 0.5;
    this.likeMinListenSeconds = 15;
    /** 阿里云整文件下载被限流时，30 秒后重试 */
    this.aliyunCacheRetryDelayMs = 30000;
    this.aliyunRetryQueue = new Map(); // cacheKey -> { timer, userId, songId, songInfo }
    this.isInitialized = false;
    
    this.init();
  }

  /**
   * 初始化智能缓存管理器
   */
  async init() {
    if (this.isInitialized) return;
    
    try {
      // 🚀 关键优化：将缓存验证改为后台异步执行，不阻塞用户登录
      this.backgroundCacheValidation();
      
      // 验证用户缓存隔离性（这个很快，可以保留）
      await this.validateUserCacheIsolation();
      
      this.isInitialized = true;
      console.log('🎯 [智能缓存] 初始化完成（缓存验证已转为后台执行）');
    } catch (error) {
      console.error('❌ [智能缓存] 初始化失败:', error);
    }
  }

  /**
   * 🚀 异步后台缓存验证
   */
  async backgroundCacheValidation() {
    // 延迟执行，让用户界面先加载
    setTimeout(async () => {
      try {
        console.log('🎬 [智能缓存] 开始后台缓存验证...');
        const startTime = Date.now();
        
        // 调用优化的批量验证方法
        await videoCacheService.cleanExpiredCache();
        
        const endTime = Date.now();
        console.log(`✅ [智能缓存] 后台缓存验证完成，总耗时: ${endTime - startTime}ms`);
        
        // 触发缓存验证完成事件
        this.triggerCacheEvent('validation-complete', {
          duration: endTime - startTime,
          timestamp: Date.now()
        });
        
      } catch (error) {
        console.error('❌ [智能缓存] 后台缓存验证失败:', error);
        
        // 触发缓存验证失败事件
        this.triggerCacheEvent('validation-failed', {
          error: error.message,
          timestamp: Date.now()
        });
      }
    }, 3000); // 延迟3秒执行，让用户界面先完成加载
  }

  /**
   * 获取当前用户ID
   */
  getCurrentUserId() {
    try {
      const token = localStorage.getItem('accessToken');
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.userId || payload.sub; // 兼容不同的JWT格式
      }
    } catch (error) {
      console.warn('⚠️ [智能缓存] 获取用户ID失败:', error);
    }
    return null;
  }

  /**
   * 生成缓存键
   */
  generateCacheKey(userId, songId, type = 'audio') {
    return `${userId}_${songId}_${type}`;
  }

  /**
   * 监听音频加载状态
   */
  monitorAudioLoading(audioElement, songId, songInfo = {}) {
    if (!audioElement || !songId) {
      console.warn('⚠️ [智能缓存] 音频元素或歌曲ID无效');
      return;
    }

    const userId = this.getCurrentUserId();
    if (!userId) {
      console.warn('⚠️ [智能缓存] 用户未登录，跳过音频缓存监听');
      return;
    }

    // 🎬 优化：视频版歌曲跳过音频缓存，只缓存视频
    if (songInfo.isVideoSong) {
      console.log('🎬 [智能缓存] 检测到视频版歌曲，跳过音频缓存:', songId);
      console.log('🎬 [智能缓存] 视频版歌曲将只缓存视频，不缓存音频');
      return; // 直接返回，不执行音频缓存逻辑
    }

    const cacheKey = this.generateCacheKey(userId, songId, 'audio');
    
    // 初始化加载状态
    this.audioLoadStates.set(cacheKey, {
      songId,
      userId,
      audioElement,
      songInfo,
      accumulatedListenSec: 0,
      lastCurrentTime: 0,
      lastPollTime: null,
      likeTriggered: false,
      cacheInProgress: false,
      startTime: Date.now()
    });

    console.log('🎵 [智能缓存] 开始监听播放进度（>50% 或 ≥15s 后缓存）:', songId);

    const handleTimeUpdate = () => {
      this.updateAudioListenProgress(cacheKey);
    };

    const handleEnded = () => {
      const loadState = this.audioLoadStates.get(cacheKey);
      if (!loadState || loadState.likeTriggered) return;
      loadState.likeTriggered = true;
      console.log('🎵 [智能缓存] 歌曲播完，视为喜欢并缓存:', songId);
      this.cacheAudioIfLiked(cacheKey);
    };

    const handlePause = () => {
      this.updateAudioListenProgress(cacheKey);
    };

    audioElement.addEventListener('timeupdate', handleTimeUpdate);
    audioElement.addEventListener('ended', handleEnded);
    audioElement.addEventListener('pause', handlePause);

    this.audioLoadStates.get(cacheKey).cleanup = () => {
      audioElement.removeEventListener('timeupdate', handleTimeUpdate);
      audioElement.removeEventListener('ended', handleEnded);
      audioElement.removeEventListener('pause', handlePause);
    };
  }

  /**
   * 累计收听时长，并在达到「喜欢」阈值时触发缓存
   */
  updateAudioListenProgress(cacheKey) {
    const loadState = this.audioLoadStates.get(cacheKey);
    if (!loadState || loadState.likeTriggered || loadState.cacheInProgress) return;

    const { audioElement } = loadState;
    if (!audioElement || audioElement.paused) return;

    const duration = audioElement.duration;
    const currentTime = audioElement.currentTime;
    if (!Number.isFinite(duration) || duration <= 0) return;

    const now = Date.now();
    if (loadState.lastPollTime != null) {
      const playDelta = currentTime - (loadState.lastCurrentTime ?? currentTime);
      const wallSec = (now - loadState.lastPollTime) / 1000;
      if (playDelta > 0 && playDelta <= wallSec + 0.75) {
        loadState.accumulatedListenSec += playDelta;
      }
    }
    loadState.lastPollTime = now;
    loadState.lastCurrentTime = currentTime;

    this.tryCacheIfUserLikesSong(cacheKey);
  }

  /**
   * 播放进度 ≥50% 或累计收听 ≥15 秒 → 用户喜欢 → 缓存整文件
   */
  tryCacheIfUserLikesSong(cacheKey) {
    const loadState = this.audioLoadStates.get(cacheKey);
    if (!loadState || loadState.likeTriggered || loadState.cacheInProgress) return;

    const { audioElement, songId } = loadState;
    const duration = audioElement?.duration;
    const currentTime = audioElement?.currentTime ?? 0;
    if (!Number.isFinite(duration) || duration <= 0) return;

    const progressRatio = currentTime / duration;
    const listened = loadState.accumulatedListenSec || 0;
    const liked = progressRatio >= this.likeProgressThreshold
      || listened >= this.likeMinListenSeconds;

    if (!liked) return;

    loadState.likeTriggered = true;
    console.log('🎵 [智能缓存] 用户喜欢这首歌，开始缓存音频:', songId, {
      progress: `${Math.round(progressRatio * 100)}%`,
      listenedSec: Math.round(listened)
    });
    this.cacheAudioIfLiked(cacheKey);
  }

  /**
   * 缓存音频（用户喜欢后：阿里云整文件下载，其它源直接 fetch 播放 URL）
   */
  async cacheAudioIfLiked(cacheKey) {
    const loadState = this.audioLoadStates.get(cacheKey);
    if (!loadState || loadState.cacheInProgress) return;

    const { songId, userId, audioElement, songInfo } = loadState;
    loadState.cacheInProgress = true;

    try {
      const existingCache = await musicCache.get(cacheKey);
      if (existingCache) {
        console.log('🎵 [智能缓存] 音频已缓存，跳过:', songId);
        return;
      }

      const storageType = songInfo?.audio_storage_type ?? songInfo?.audioStorageType ?? songInfo?.storageType;
      const isAliyun = storageType === 2;

      if (isAliyun) {
        await this.downloadAndCacheAliyunAudio(userId, songId, cacheKey, songInfo);
        return;
      }

      const audioUrl = audioElement.src;
      if (!audioUrl || audioUrl.startsWith('blob:')) {
        console.log('🎵 [智能缓存] 跳过 blob URL 的缓存:', songId);
        return;
      }

      console.log('🎵 [智能缓存] 开始下载并缓存音频:', songId);
      const response = await fetch(audioUrl);
      if (!response.ok) {
        throw new Error(`下载失败: ${response.status} ${response.statusText}`);
      }

      const audioBlob = await response.blob();
      await this.saveAudioCacheAndNotify(cacheKey, songId, userId, audioBlob, songInfo);
    } catch (error) {
      console.error('❌ [智能缓存] 音频缓存失败:', error);
    } finally {
      if (loadState) {
        loadState.cacheInProgress = false;
      }
      this.cleanupAudioMonitoring(cacheKey);
    }
  }

  /**
   * 阿里云盘：通过 download-url 整文件下载（no-referrer），写入 music-files
   */
  async downloadAndCacheAliyunAudio(userId, songId, cacheKey, songInfo, refresh = true) {
    try {
      const existingCache = await musicCache.get(cacheKey);
      if (existingCache) {
        console.log('🎵 [智能缓存] 阿里云音频已缓存，跳过:', songId);
        return true;
      }

      console.log('🎵 [智能缓存] 阿里云整文件下载缓存:', songId);
      const query = refresh ? '?refresh=true' : '';
      const resp = await myRequest.get(`/api/aliyun/media/download-url/${songId}${query}`);
      if (!resp?.url) {
        throw new Error('无法获取阿里云下载链接');
      }

      const response = await fetch(resp.url, {
        referrerPolicy: 'no-referrer',
        credentials: 'omit'
      });

      if (response.status === 403 || response.status === 429) {
        console.warn('⚠️ [智能缓存] 阿里云下载受限，30 秒后重试:', songId, response.status);
        this.scheduleAliyunCacheRetry(cacheKey, userId, songId, songInfo);
        return false;
      }

      if (!response.ok) {
        throw new Error(`阿里云下载失败: ${response.status}`);
      }

      let audioBlob = await response.blob();
      if (!audioBlob?.size) {
        throw new Error('阿里云下载结果为空');
      }
      // 按文件头纠正 MIME，避免假 .mp3 缓存后 iOS 播放 code=4
      audioBlob = await retypeAudioBlob(audioBlob, resp.mimeType || 'audio/mpeg');

      await this.saveAudioCacheAndNotify(cacheKey, songId, userId, audioBlob, songInfo);
      this.cancelAliyunCacheRetry(cacheKey);
      return true;
    } catch (error) {
      const msg = String(error?.message || error);
      if (msg.includes('403') || msg.includes('429') || msg.includes('限流')) {
        this.scheduleAliyunCacheRetry(cacheKey, userId, songId, songInfo);
        return false;
      }
      throw error;
    }
  }

  scheduleAliyunCacheRetry(cacheKey, userId, songId, songInfo) {
    if (this.aliyunRetryQueue.has(cacheKey)) {
      console.log('🎵 [智能缓存] 已有阿里云缓存重试计划:', songId);
      return;
    }

    console.log(`🎵 [智能缓存] 记录「用户喜欢」${songId}，${this.aliyunCacheRetryDelayMs / 1000}s 后重试下载`);
    const timer = setTimeout(async () => {
      this.aliyunRetryQueue.delete(cacheKey);
      try {
        await this.downloadAndCacheAliyunAudio(userId, songId, cacheKey, songInfo, true);
      } catch (e) {
        console.error('❌ [智能缓存] 阿里云缓存重试失败:', songId, e);
      }
    }, this.aliyunCacheRetryDelayMs);

    this.aliyunRetryQueue.set(cacheKey, { timer, userId, songId, songInfo });
  }

  cancelAliyunCacheRetry(cacheKey) {
    const pending = this.aliyunRetryQueue.get(cacheKey);
    if (pending?.timer) {
      clearTimeout(pending.timer);
      this.aliyunRetryQueue.delete(cacheKey);
    }
  }

  async saveAudioCacheAndNotify(cacheKey, songId, userId, audioBlob, songInfo) {
    await musicCache.set(cacheKey, audioBlob);

    const verifyCached = await musicCache.get(cacheKey);
    if (!verifyCached) {
      await musicCache.set(cacheKey, audioBlob);
    }

    console.log('✅ [智能缓存] 音频缓存成功:', songId, '大小:', Math.round(audioBlob.size / 1024 / 1024), 'MB');

    if (songInfo?.isVideoSong) {
      this.startVideoCacheForVideoSong(songId, userId, songInfo);
    }

    this.triggerCacheEvent('audio-cached', {
      songId,
      userId,
      cacheKey,
      size: audioBlob.size,
      songInfo
    });
  }

  /**
   * 🔥 新增：为视频版歌曲启动视频缓存
   */
  async startVideoCacheForVideoSong(songId, userId, songInfo) {
    if (!isVideoSongAutoCacheEnabled()) {
      console.log('🎬 [智能缓存] 视频版自动缓存已关闭，跳过:', songId);
      return;
    }
    try {
      console.log('🎬 [智能缓存] 开始为视频版歌曲启动视频缓存:', songId);
      
      // 检查是否已经有视频缓存
      const hasVideoCache = await videoCacheService.hasFullVideoCache(userId, songId);
      if (hasVideoCache) {
        console.log('🎬 [智能缓存] 视频已缓存，跳过:', songId);
        return;
      }
      
      // 获取视频URL（从歌曲信息中获取）
      const videoUrl = songInfo.videoUrl || songInfo.streamUrl;
      if (!videoUrl || videoUrl.startsWith('blob:')) {
        console.log('🎬 [智能缓存] 视频URL无效，跳过视频缓存:', songId, 'URL:', videoUrl);
        return;
      }
      
      console.log('🎬 [智能缓存] 开始下载并缓存视频:', songId, 'URL:', videoUrl);
      
      // 使用视频缓存服务下载并缓存视频
      await videoCacheService.downloadAndCacheVideo(
        videoUrl, 
        userId, 
        songId,
        (progress, loaded, total) => {
          console.log(`🎬 [智能缓存] 视频下载进度: ${progress}%`);
        }
      );
      
      console.log('✅ [智能缓存] 视频版歌曲视频缓存成功:', songId);
      
      // 触发视频缓存成功事件
      this.triggerCacheEvent('video-cached', {
        songId,
        userId,
        songInfo
      });
      
    } catch (error) {
      console.error('❌ [智能缓存] 视频版歌曲视频缓存失败:', error);
    }
  }

  /**
   * 监听视频加载状态
   */
  monitorVideoLoading(videoElement, songId, songInfo = {}) {
    if (!isVideoSongAutoCacheEnabled()) {
      console.log('🎬 [智能缓存] 视频版自动缓存已关闭，跳过视频加载监听:', songId);
      return;
    }
    if (!videoElement || !songId) {
      console.warn('⚠️ [智能缓存] 视频元素或歌曲ID无效');
      return;
    }

    const userId = this.getCurrentUserId();
    if (!userId) {
      console.warn('⚠️ [智能缓存] 用户未登录，跳过视频缓存监听');
      return;
    }

    const cacheKey = this.generateCacheKey(userId, songId, 'video');
    
    // 初始化加载状态
    this.videoLoadStates.set(cacheKey, {
      songId,
      userId,
      videoElement,
      songInfo,
      isLoaded: false,
      loadProgress: 0,
      lastBuffered: 0,
      startTime: Date.now()
    });

    console.log('🎬 [智能缓存] 开始监听视频加载状态:', songId);

    // 监听视频加载进度
    const handleProgress = () => {
      this.updateVideoLoadProgress(cacheKey);
    };

    // 监听视频可以播放
    const handleCanPlay = () => {
      this.updateVideoLoadProgress(cacheKey);
    };

    // 监听视频可以播放到结束
    const handleCanPlayThrough = () => {
      this.updateVideoLoadProgress(cacheKey);
    };

    // 监听视频加载完成
    const handleLoadedData = () => {
      this.updateVideoLoadProgress(cacheKey);
    };

    // 监听视频结束（用户播放完毕）
    const handleEnded = () => {
      this.handleVideoPlayedToEnd(cacheKey);
    };

    // 监听视频暂停（用户可能已经看够了）
    const handlePause = () => {
      this.handleVideoPaused(cacheKey);
    };

    // 绑定事件监听器
    videoElement.addEventListener('progress', handleProgress);
    videoElement.addEventListener('canplay', handleCanPlay);
    videoElement.addEventListener('canplaythrough', handleCanPlayThrough);
    videoElement.addEventListener('loadeddata', handleLoadedData);
    videoElement.addEventListener('ended', handleEnded);
    videoElement.addEventListener('pause', handlePause);

    // 存储清理函数
    this.videoLoadStates.get(cacheKey).cleanup = () => {
      videoElement.removeEventListener('progress', handleProgress);
      videoElement.removeEventListener('canplay', handleCanPlay);
      videoElement.removeEventListener('canplaythrough', handleCanPlayThrough);
      videoElement.removeEventListener('loadeddata', handleLoadedData);
      videoElement.removeEventListener('ended', handleEnded);
      videoElement.removeEventListener('pause', handlePause);
    };
  }

  /**
   * 更新视频加载进度
   */
  updateVideoLoadProgress(cacheKey) {
    const loadState = this.videoLoadStates.get(cacheKey);
    if (!loadState) return;

    const { videoElement, songId } = loadState;
    
    try {
      // 检查视频是否已加载完成
      if (videoElement.readyState >= 4) { // HAVE_ENOUGH_DATA
        const duration = videoElement.duration;
        const buffered = videoElement.buffered;
        
        console.log('🎬 [智能缓存] 视频状态检查:', {
          songId,
          readyState: videoElement.readyState,
          duration,
          bufferedLength: buffered.length,
          videoUrl: videoElement.src
        });
        
        if (duration > 0 && buffered.length > 0) {
          const bufferedEnd = buffered.end(buffered.length - 1);
          const loadProgress = bufferedEnd / duration;
          
          loadState.loadProgress = loadProgress;
          loadState.lastBuffered = bufferedEnd;
          
          console.log('🎬 [智能缓存] 视频加载进度:', {
            songId,
            loadProgress: Math.round(loadProgress * 100) + '%',
            cacheThreshold: Math.round(this.cacheThreshold * 100) + '%',
            isLoaded: loadState.isLoaded
          });
          
          // 如果加载进度超过阈值，立即缓存（不再延迟）
          if (loadProgress >= this.cacheThreshold && !loadState.isLoaded) {
            loadState.isLoaded = true;
            console.log('🎬 [智能缓存] 视频加载完成，立即开始缓存:', songId, '进度:', Math.round(loadProgress * 100) + '%');
            
            // 立即缓存，不再延迟
            this.cacheVideoIfLiked(cacheKey);
          }
        } else {
          console.log('🎬 [智能缓存] 视频缓冲数据不足:', {
            songId,
            duration,
            bufferedLength: buffered.length
          });
        }
      } else {
        console.log('🎬 [智能缓存] 视频未加载完成:', {
          songId,
          readyState: videoElement.readyState
        });
      }
    } catch (error) {
      console.warn('⚠️ [智能缓存] 更新视频加载进度失败:', error);
    }
  }

  /**
   * 处理视频播放到结束
   */
  handleVideoPlayedToEnd(cacheKey) {
    const loadState = this.videoLoadStates.get(cacheKey);
    if (!loadState) return;

    console.log('🎬 [智能缓存] 视频播放到结束，用户可能喜欢这首歌:', loadState.songId);
    
    // 如果视频已加载完成，立即缓存
    if (loadState.isLoaded) {
      this.cacheVideoIfLiked(cacheKey);
    }
  }

  /**
   * 处理视频暂停
   */
  handleVideoPaused(cacheKey) {
    const loadState = this.videoLoadStates.get(cacheKey);
    if (!loadState) return;

    // 如果用户暂停了视频，且已经播放了较长时间，也考虑缓存
    const playTime = Date.now() - loadState.startTime;
    const playedLongEnough = playTime > 30000; // 播放超过30秒

    if (loadState.isLoaded && playedLongEnough) {
      console.log('🎬 [智能缓存] 视频暂停，用户可能喜欢这首歌:', loadState.songId, '播放时长:', Math.round(playTime / 1000) + '秒');
      this.cacheVideoIfLiked(cacheKey);
    }
  }

  /**
   * 缓存视频（如果用户喜欢）
   */
  async cacheVideoIfLiked(cacheKey) {
    const loadState = this.videoLoadStates.get(cacheKey);
    if (!loadState) {
      console.log('🎬 [智能缓存] 缓存状态不存在:', cacheKey);
      return;
    }

    const { songId, userId, videoElement, songInfo } = loadState;

    console.log('🎬 [智能缓存] 开始检查视频缓存条件:', {
      songId,
      userId,
      videoUrl: videoElement?.src,
      hasVideoElement: !!videoElement
    });

    try {
      // 检查是否已经缓存
      const existingCache = await videoCacheService.hasFullVideoCache(userId, songId);
      if (existingCache) {
        console.log('🎬 [智能缓存] 视频已缓存，跳过:', songId);
        return;
      }

      // 获取视频URL
      const videoUrl = videoElement.src;
      if (!videoUrl || videoUrl.startsWith('blob:')) {
        console.log('🎬 [智能缓存] 跳过blob URL的缓存:', songId, 'URL:', videoUrl);
        return;
      }

      console.log('🎬 [智能缓存] 开始缓存视频:', songId, 'URL:', videoUrl);

      // 使用视频缓存服务下载并缓存视频
      await videoCacheService.downloadAndCacheVideo(
        videoUrl, 
        userId, 
        songId,
        (progress, loaded, total) => {
          console.log(`🎬 [智能缓存] 视频下载进度: ${progress}%`);
        }
      );
      
      console.log('✅ [智能缓存] 视频缓存成功:', songId);

      // 🔥 新增：缓存完成后立即切换到缓存源，节省流量
      await this.switchToCachedVideo(userId, songId, videoElement);

      // 触发缓存成功事件
      this.triggerCacheEvent('video-cached', {
        songId,
        userId,
        cacheKey,
        songInfo
      });

    } catch (error) {
      console.error('❌ [智能缓存] 视频缓存失败:', error);
    } finally {
      // 清理监听器
      this.cleanupVideoMonitoring(cacheKey);
    }
  }

  /**
   * 🔥 新增：缓存完成后立即切换到缓存源
   */
  async switchToCachedVideo(userId, songId, videoElement) {
    try {
      console.log('🔄 [智能缓存] 开始切换到视频缓存源:', songId);
      
      // 获取完整视频缓存
      const cachedVideo = await videoCacheService.getFullVideoCache(userId, songId);
      if (!cachedVideo || !cachedVideo.blobUrl) {
        console.log('🎬 [智能缓存] 未找到完整视频缓存，跳过切换:', songId);
        return false;
      }
      
      console.log('🎬 [智能缓存] 找到视频缓存，立即切换:', songId, '缓存大小:', cachedVideo.size);
      
      // 保存当前播放状态
      const currentTime = videoElement.currentTime;
      const isPlaying = !videoElement.paused;
      const volume = videoElement.volume;
      
      // 切换到缓存源
      videoElement.src = cachedVideo.blobUrl;
      videoElement.load();
      
      // 等待视频加载
      await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('视频缓存加载超时'));
        }, 2000);
        
        const onCanPlay = () => {
          clearTimeout(timeout);
          videoElement.removeEventListener('canplay', onCanPlay);
          videoElement.removeEventListener('error', onError);
          resolve();
        };
        
        const onError = (error) => {
          clearTimeout(timeout);
          videoElement.removeEventListener('canplay', onCanPlay);
          videoElement.removeEventListener('error', onError);
          reject(error);
        };
        
        videoElement.addEventListener('canplay', onCanPlay);
        videoElement.addEventListener('error', onError);
      });
      
      // 恢复播放状态
      videoElement.currentTime = currentTime;
      videoElement.volume = volume;
      
      if (isPlaying) {
        await videoElement.play();
      }
      
      console.log('✅ [智能缓存] 成功切换到视频缓存源，节省流量:', songId);
      return true;
      
    } catch (error) {
      console.error('❌ [智能缓存] 切换到视频缓存源失败:', error);
      return false;
    }
  }

  /**
   * 清理音频监听
   */
  cleanupAudioMonitoring(cacheKey) {
    const loadState = this.audioLoadStates.get(cacheKey);
    if (loadState && loadState.cleanup) {
      loadState.cleanup();
    }
    this.audioLoadStates.delete(cacheKey);
  }

  /**
   * 清理视频监听
   */
  cleanupVideoMonitoring(cacheKey) {
    const loadState = this.videoLoadStates.get(cacheKey);
    if (loadState && loadState.cleanup) {
      loadState.cleanup();
    }
    this.videoLoadStates.delete(cacheKey);
  }

  /**
   * 快速获取用户所有音频缓存歌曲ID（毫秒级查询）
   * @param {string} userId - 用户ID
   * @returns {Promise<Set>} 返回Set，包含所有有音频缓存的歌曲ID
   */
  async getCachedAudioSongIds(userId) {
    try {
      console.log('🎵 [智能缓存] 快速查询用户音频缓存歌曲ID:', userId);
      
      // 获取所有音频缓存键
      const allKeys = await musicCache.getCacheKeys();
      console.log('🎵 [智能缓存] 所有音频缓存键数:', allKeys.length);
      
      // 筛选出该用户的音频缓存
      const userAudioKeys = allKeys.filter(key => {
        // 音频缓存键格式：userId_songId_audio
        const parts = key.split('_');
        console.log('🎵 [智能缓存] 检查音频缓存键:', key, 'parts:', parts, 'userId:', userId, 'userId类型:', typeof userId);
        return parts.length === 3 && parts[0] === String(userId) && parts[2] === 'audio';
      });
      
      // 提取歌曲ID
      const cachedSongIds = new Set();
      userAudioKeys.forEach(key => {
        const parts = key.split('_');
        if (parts.length === 3) {
          const songId = parts[1]; // songId
          cachedSongIds.add(songId);
          console.log(`🎵 [智能缓存] 添加音频缓存歌曲ID: ${songId} (类型: ${typeof songId})`);
        }
      });
      
      console.log('🎵 [智能缓存] 快速查询完成，音频缓存歌曲数:', cachedSongIds.size);
      return cachedSongIds;
      
    } catch (error) {
      console.error('❌ [智能缓存] 快速查询音频缓存歌曲ID失败:', error);
      return new Set();
    }
  }

  /**
   * 检查音频是否已缓存
   */
  async isAudioCached(songId) {
    const userId = this.getCurrentUserId();
    if (!userId) return false;

    const cacheKey = this.generateCacheKey(userId, songId, 'audio');
    const cached = await musicCache.get(cacheKey);
    return !!cached;
  }

  /**
   * 检查视频是否已缓存
   */
  async isVideoCached(songId) {
    const userId = this.getCurrentUserId();
    if (!userId) return false;

    const cached = await videoCacheService.hasFullVideoCache(userId, songId);
    return !!cached;
  }

  /**
   * 快速获取用户所有缓存歌曲ID（音频+视频，毫秒级查询）
   * @param {string} userId - 用户ID
   * @returns {Promise<Object>} 返回对象，包含audio和video的缓存歌曲ID集合
   */
  async getAllCachedSongIds(userId) {
    try {
      console.log('🎯 [智能缓存] 快速查询用户所有缓存歌曲ID:', userId);
      
      // 并行获取音频缓存、视频(整文件)缓存、HLS 分片完整缓存
      const segmentModule = import('@/services/videoSegmentCache.js')
        .then(m => m.getFullyCachedVideoIds())
        .catch(() => new Set());
      const [audioCachedIds, videoFullFileIds, fullyCachedVideoIds] = await Promise.all([
        this.getCachedAudioSongIds(userId),
        videoCacheService.getCachedSongIds(userId),
        segmentModule
      ]);

      // HLS 分片缓存维度 m{songId} → 还原 songId 并入视频集合
      const videoCachedIds = new Set(videoFullFileIds);
      for (const vid of fullyCachedVideoIds) {
        if (typeof vid === 'string' && vid.startsWith('m')) {
          const sid = vid.slice(1);
          if (/^\d+$/.test(sid)) {
            videoCachedIds.add(sid);
            videoCachedIds.add(Number(sid));
          }
        }
      }
      
      // 合并所有缓存歌曲ID
      const allCachedIds = new Set([...audioCachedIds, ...videoCachedIds]);
      
      console.log('🎯 [智能缓存] 快速查询完成:', {
        audio: audioCachedIds.size,
        video: videoCachedIds.size,
        total: allCachedIds.size
      });
      
      return {
        audio: audioCachedIds,
        video: videoCachedIds,
        all: allCachedIds
      };
      
    } catch (error) {
      console.error('❌ [智能缓存] 快速查询所有缓存歌曲ID失败:', error);
      return {
        audio: new Set(),
        video: new Set(),
        all: new Set()
      };
    }
  }

  /**
   * 获取缓存的音频URL
   */
  async getCachedAudioUrl(songId) {
    const userId = this.getCurrentUserId();
    if (!userId) return null;

    const cacheKey = this.generateCacheKey(userId, songId, 'audio');
    const blob = await musicCache.get(cacheKey);
    
    if (blob) {
      return URL.createObjectURL(blob);
    }
    
    return null;
  }

  /**
   * 获取缓存的视频URL
   */
  async getCachedVideoUrl(songId) {
    const userId = this.getCurrentUserId();
    if (!userId) return null;

    const cached = await videoCacheService.getFullVideoCache(userId, songId);
    
    if (cached) {
      return cached.blobUrl;
    }
    
    return null;
  }

  /**
   * 🔥 永久缓存：不再清理过期缓存，缓存永久保存
   * 只有用户主动清理或缓存损坏时才会删除
   * 🚀 优化：支持后台异步执行
   */
  async cleanExpiredCache(isBackground = false) {
    try {
      if (isBackground) {
        console.log('🎯 [智能缓存] 后台模式：永久缓存模式，跳过过期缓存清理，缓存永久保存');
      } else {
        console.log('🎯 [智能缓存] 永久缓存模式：跳过过期缓存清理，缓存永久保存');
      }
      
      // 🚀 使用优化的批量验证方法清理损坏缓存
      await videoCacheService.cleanExpiredCache();
      
      if (isBackground) {
        console.log('✅ [智能缓存] 后台缓存验证：只清理了损坏的缓存');
      } else {
        console.log('✅ [智能缓存] 永久缓存模式：只清理了损坏的缓存');
      }
    } catch (error) {
      console.error('❌ [智能缓存] 清理损坏缓存失败:', error);
    }
  }

  /**
   * 验证用户缓存隔离性
   * 确保不同用户的缓存不会混淆
   */
  async validateUserCacheIsolation() {
    try {
      const result = await videoCacheService.validateUserCacheIsolation();
      if (result.isValid) {
        console.log('✅ [智能缓存] 用户缓存隔离验证通过');
        if (result.userStats) {
          const totalUsers = Object.keys(result.userStats).length;
          console.log(`📊 [智能缓存] 当前共有 ${totalUsers} 个用户的缓存数据`);
        }
      } else {
        console.warn('⚠️ [智能缓存] 用户缓存隔离验证失败:', result.message);
      }
      return result;
    } catch (error) {
      console.error('❌ [智能缓存] 用户缓存隔离验证异常:', error);
      return { isValid: false, message: '验证异常', error };
    }
  }

  /**
   * 获取缓存统计信息
   */
  async getCacheStats() {
    try {
      const audioCacheSize = await musicCache.getCacheSize();
      const videoCacheStats = await videoCacheService.getCacheStats();
      
      return {
        audio: {
          size: Math.round(audioCacheSize / 1024 / 1024), // MB
          sizeBytes: audioCacheSize
        },
        video: videoCacheStats
      };
    } catch (error) {
      console.error('❌ [智能缓存] 获取缓存统计失败:', error);
      return null;
    }
  }

  /**
   * 触发缓存事件
   */
  triggerCacheEvent(eventType, data) {
    const eventName = `smart-cache-${eventType}`;
    console.log('🎵 [智能缓存] 触发缓存事件:', eventName, data);
    const event = new CustomEvent(eventName, {
      detail: data
    });
    window.dispatchEvent(event);
    console.log('🎵 [智能缓存] 事件已分发:', eventName);
  }

  /**
   * 清除指定歌曲的音频缓存
   */
  async clearSongAudioCache(userId, songId) {
    try {
      console.log('🧹 [智能缓存] 开始清除歌曲音频缓存:', { userId, songId });

      let clearedAny = false;
      const cacheKey = `${userId}_${songId}_audio`;

      this.cancelAliyunCacheRetry(cacheKey);

      const audioBlob = await musicCache.get(cacheKey);
      if (audioBlob) {
        await musicCache.delete(cacheKey);
        clearedAny = true;
        console.log('✅ [智能缓存] 整文件音频缓存已清除:', cacheKey);
      }

      try {
        const { clearSongAliyunRangeCache } = await import('@/services/audioRangeCache.js');
        const rangeResult = await clearSongAliyunRangeCache(songId);
        if (rangeResult?.deleted) {
          clearedAny = true;
          console.log('✅ [智能缓存] 阿里云 Range 音频缓存已清除:', songId, rangeResult.audioId);
        }
      } catch (rangeError) {
        console.warn('⚠️ [智能缓存] 清除阿里云 Range 缓存失败:', rangeError);
      }

      if (!clearedAny) {
        console.log('ℹ️ [智能缓存] 歌曲无音频缓存可清除:', songId);
        return { success: true, cleared: false, message: '音频缓存不存在' };
      }

      return { success: true, cleared: true, message: '音频缓存清除成功' };
    } catch (error) {
      console.error('❌ [智能缓存] 清除歌曲音频缓存失败:', error);
      return { success: false, cleared: false, message: error.message };
    }
  }

  /**
   * 停止所有监听
   */
  stopAllMonitoring() {
    // 清理所有音频监听
    for (const cacheKey of this.audioLoadStates.keys()) {
      this.cleanupAudioMonitoring(cacheKey);
    }
    
    // 清理所有视频监听
    for (const cacheKey of this.videoLoadStates.keys()) {
      this.cleanupVideoMonitoring(cacheKey);
    }
    
    console.log('🛑 [智能缓存] 已停止所有监听');
  }
}

// 创建单例实例
const smartCacheManager = new SmartCacheManager();

export default smartCacheManager;
